import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { addMonths, addDays, differenceInDays, parseISO, isValid, format, startOfDay } from 'date-fns';
import { useMediaQuery } from 'react-responsive';
import * as TravelActions from '../../store/travel/actions';
import * as AvailabilityThunks from '../../store/availability/thunks';
import AvailabilityGuests from './Guests';
import PackageSelector from './PackageSelector';
import DateRangeCalendar from './DateRangeCalendar';
import {
  toDateFnsToken,
  getCurrentHourInTimeZone,
} from '../../helpers/date-time-helper';
import ToasterHelper from '../../helpers/toaster-helper';
import StringHelper from '../../helpers/string-helper';
import UrlHelper from '../../helpers/url-helper';
import { STEP_AVAILABILITY, STEP_ROOMS } from '../../helpers/steps';

const Availability = () => {
  const { t, i18n } = useTranslation();
  // 2 meses quando há espaço lateral; senão 1 mês (sem remontar o calendário,
  // então o popover não fecha ao redimensionar)
  const isWideCalendar = useMediaQuery({ minWidth: 992 });
  const history = useHistory();
  const dispatch = useDispatch();
  const travel = useSelector(state => state.travel);
  const reservation = useSelector(state => state.reservation);
  const application = useSelector(state => state.application);
  const availabilityRooms = useSelector(state => state.availability.rooms);
  const productsAds = useSelector(state => state.products.ads);
  const { couponCode } = reservation;
  const { widget, currentStep } = application;
  const isWidget = widget === '1';
  const property = useSelector(state => state.property);
  const {
    name,
    config: propertyConfig
  } = property;
  const {
    releasePeriod,
    releaseHours,
    systemTimezone,
    nightsLimit,
    reservationMaxMonthsAhead,
    packageSale,
    packages,
    prioritizeProducts,
  } = propertyConfig;
  const {
    loaded: travelLoaded,
    arrivalDate: currentArrivalDate,
    departureDate: currentDepartureDate,
    totalNights,
    totalAdults,
    totalChildren,
    children,
    selectedPackageId,
  } = travel;

  const maxTotalGuests = availabilityRooms.length > 0
    ? Object.values(
        availabilityRooms.reduce((acc, room) => {
          const maxBase = room.salesBaseRooms.length > 0
            ? Math.max(...room.salesBaseRooms.map(sb => sb.base))
            : 0;
          if (!acc[room.roomTypeRealId]) {
            acc[room.roomTypeRealId] = { maxBase: 0, available: room.available };
          }
          acc[room.roomTypeRealId].maxBase = Math.max(acc[room.roomTypeRealId].maxBase, maxBase);
          return acc;
        }, {})
      ).reduce((sum, { maxBase, available }) => sum + maxBase * available, 0)
    : undefined;

  useEffect(() => {
    if (maxTotalGuests > 0 && totalAdults > maxTotalGuests) {
      dispatch(TravelActions.setTotalAdults(maxTotalGuests));
    }
  }, [maxTotalGuests, totalAdults, dispatch]);

  const selectedPackage = packageSale
    ? packages.find(p => p.id === selectedPackageId) ?? null
    : null;
  const [arrivalDate, setArrivalDate] = useState(null);
  const [departureDate, setDepartureDate] = useState(null);
  const [fetching, setFetching] = useState(false);
  const showPersons = true;
  const today = startOfDay(new Date());
  // limite de meses à frente: usa o config; se ausente/0, fallback de 12 meses
  const monthsAhead =
    +reservationMaxMonthsAhead > 0 ? +reservationMaxMonthsAhead : 12;
  const maxDate = addMonths(today, monthsAhead);
  // Antecedência mínima em dias: com releasePeriod dias, a chegada mais próxima
  // é hoje + releasePeriod.
  const releaseDays = +releasePeriod > 0 ? +releasePeriod : 0;
  // Corte por horas: só vale quando não há corte por dias (releasePeriod > 0 já
  // bloqueia o mesmo dia). Passado o horário-limite da propriedade, hoje deixa
  // de ser oferecido como chegada.
  const sameDayCheckinClosed =
    releaseDays === 0 &&
    +releaseHours > 0 &&
    getCurrentHourInTimeZone(systemTimezone) >= +releaseHours;
  // O backend recusa essas datas de qualquer forma; aqui é só para o hóspede
  // não conseguir escolhê-las.
  const minDate = addDays(today, sameDayCheckinClosed ? 1 : releaseDays);

  if (arrivalDate === null && currentArrivalDate !== null) {
    setArrivalDate(parseISO(currentArrivalDate));
  }

  if (departureDate === null && currentDepartureDate !== null) {
    setDepartureDate(parseISO(currentDepartureDate));
  }

  // region Events Handler

  const clearDates = () => {
    setArrivalDate(null);
    setDepartureDate(null);
    dispatch(TravelActions.setArrivalDate(null));
    dispatch(TravelActions.setDepartureDate(null));
    dispatch(TravelActions.setTotalNights(0));
  };

  const goToRooms = (arrival, departure) => {
    history.push(
      UrlHelper.to('rooms', {
        arrival,
        departure,
        coupon: couponCode,
        adults: totalAdults,
        children: totalChildren,
        widget: widget ?? '',
      })
    );
  };

  const onDatesChangeHandler = ({ startDate, endDate }) => {
    const realStartDate = startDate;
    let realEndDate = endDate;
    let nights = 0;

    if (packageSale && selectedPackage && isValid(startDate)) {
      const { nights: pkgNights } = selectedPackage;
      nights = pkgNights;
      if (+nightsLimit > 0 && nights > nightsLimit) {
        ToasterHelper.error(t('errors.nightsLimit', { nights: nightsLimit }));
        clearDates();
        return;
      }
      realEndDate = addDays(startDate, nights);
    } else if (!packageSale) {
      if (isValid(startDate) && isValid(endDate)) {
        nights = differenceInDays(endDate, startDate);
        if (+nightsLimit > 0 && nights > nightsLimit) {
          ToasterHelper.error(t('errors.nightsLimit', { nights: nightsLimit }));
          realEndDate = null;
        }
      }
    }

    setArrivalDate(realStartDate);
    setDepartureDate(realEndDate);

    let formattedArrivalDate = null;
    let formattedDepartureDate = null;

    if (isValid(realStartDate)) {
      formattedArrivalDate = format(realStartDate, 'yyyy-MM-dd');
    }

    if (isValid(realEndDate)) {
      formattedDepartureDate = format(realEndDate, 'yyyy-MM-dd');
    }

    dispatch(TravelActions.setArrivalDate(formattedArrivalDate));
    dispatch(TravelActions.setDepartureDate(formattedDepartureDate));
    dispatch(TravelActions.setTotalNights(nights));
  };

  const onPackageChangeHandler = id => {
    dispatch(TravelActions.setSelectedPackageId(id));

    const pkg = packages.find(p => p.id === id);

    // Sem pacote válido ou sem data de entrada ainda → nada a recalcular
    if (!pkg || StringHelper.isEmpty(currentArrivalDate)) {
      clearDates();
      return;
    }

    const { nights } = pkg;

    if (+nightsLimit > 0 && nights > nightsLimit) {
      ToasterHelper.error(t('errors.nightsLimit', { nights: nightsLimit }));
      clearDates();
      return;
    }

    // Mantém a data de entrada e recalcula a saída com as noites do novo pacote
    const start = parseISO(currentArrivalDate);
    const departureStr = format(addDays(start, nights), 'yyyy-MM-dd');

    setArrivalDate(start);
    setDepartureDate(addDays(start, nights));
    dispatch(TravelActions.setArrivalDate(currentArrivalDate));
    dispatch(TravelActions.setDepartureDate(departureStr));
    dispatch(TravelActions.setTotalNights(nights));

    // Modo priorizar produtos: trocar de pacote na página de Quartos (pág. 3)
    // reinicia o fluxo no Check-in (pág. 1). O novo pacote fica selecionado e as
    // datas recalculadas acima persistem no Redux (loadProperty não as reseta em
    // modo pacote).
    const isPrioritizeRooms =
      packageSale &&
      prioritizeProducts &&
      Array.isArray(productsAds) &&
      productsAds.length > 0 &&
      currentStep === STEP_ROOMS;

    if (isPrioritizeRooms && id !== selectedPackageId) {
      history.push(UrlHelper.to('', { widget: widget ?? '' }));
      return;
    }

    // Já está na página de resultados → re-consulta com as novas noites.
    // As mudanças de saída/noites acima resetam travel.loaded, liberando o
    // fetch (mesmo mecanismo do "Consultar" e da troca de hóspedes).
    if (travelLoaded) {
      setFetching(true);
      dispatch(
        AvailabilityThunks.fetchAvailability(
          currentArrivalDate,
          departureStr,
          couponCode,
          totalAdults,
          totalChildren,
          children,
          () => {
            setFetching(false);
            goToRooms(currentArrivalDate, departureStr);
          },
          () => {
            setFetching(false);
            ToasterHelper.error(
              releasePeriod > 0
                ? t('errors.availabilityEmptyResultset', {
                    period: releasePeriod,
                    plural: releasePeriod > 1 ? 's' : '',
                  })
                : t('errors.availabilityEmptyResultsetReleasePeriod')
            );
          }
        )
      );
    }
  };

  const onPersonChangeHandler = (updatedAdults, updatedChildren) => {
    if (updatedAdults !== null) {
      dispatch(TravelActions.setTotalAdults(updatedAdults));
    }

    if (updatedChildren !== null) {
      dispatch(TravelActions.setTotalChildren(updatedChildren));
    }
  };

  const onChildAgeChangeHandler = (age, index) => {
    dispatch(TravelActions.setChildAge(age, index));
  };

  const onAvailabilityFetchHandler = () => {
    if (fetching) {
      ToasterHelper.error(t('errors.fetchingAvailability'));
      return;
    }

    if (
      StringHelper.isEmpty(currentArrivalDate) ||
      StringHelper.isEmpty(currentDepartureDate)
    ) {
      ToasterHelper.error(t('errors.emptyAvailabilityDates'));
      return;
    }

    if (+nightsLimit > 0 && totalNights > nightsLimit) {
      ToasterHelper.error(t('errors.nightsLimit', { nights: nightsLimit }));
      return;
    }

    const navigateToRooms = () => {
      const isPrioritize = packageSale && prioritizeProducts && Array.isArray(productsAds) && productsAds.length > 0;
      if (isPrioritize && currentStep === STEP_AVAILABILITY) {
        history.push(UrlHelper.to('products', {
          arrival: currentArrivalDate,
          departure: currentDepartureDate,
          coupon: couponCode,
          adults: totalAdults,
          children: totalChildren,
          widget: widget ?? '',
        }));
      } else {
        goToRooms(currentArrivalDate, currentDepartureDate);
      }
    };

    if (travelLoaded) {
      navigateToRooms();
      return;
    }

    const isReConsult = packageSale && prioritizeProducts && currentStep !== STEP_AVAILABILITY;

    setFetching(true);

    dispatch(
      AvailabilityThunks.fetchAvailability(
        currentArrivalDate,
        currentDepartureDate,
        couponCode,
        totalAdults,
        totalChildren,
        children,
        () => {
          setFetching(false);
          navigateToRooms();
        },
        () => {
          setFetching(false);

          if (releasePeriod > 0) {
            ToasterHelper.error(
              t('errors.availabilityEmptyResultset', {
                period: releasePeriod,
                plural: releasePeriod > 1 ? 's' : '',
              })
            );
          } else {
            ToasterHelper.error(
              t('errors.availabilityEmptyResultsetReleasePeriod')
            );
          }
        },
        false,
        isReConsult
      )
    );
  };

  // Confirmar no seletor de hóspedes só dispara a busca se já há datas
  // escolhidas; senão apenas aplica os hóspedes e fecha (sem erro de datas,
  // pois a busca em si é feita pelo botão "Consultar").
  const onGuestsConfirmHandler = () => {
    if (
      StringHelper.isNotEmpty(currentArrivalDate) &&
      StringHelper.isNotEmpty(currentDepartureDate)
    ) {
      onAvailabilityFetchHandler();
    }
  };

  // endregion

  // region Calendar
  const language = i18n.language ? i18n.language.toLowerCase() : 'pt-br';

  const calendarProps = {
    startDate: arrivalDate,
    endDate: departureDate,
    onDatesChange: onDatesChangeHandler,
    onClear: clearDates,
    singleMode: !!packageSale,
    packageNights: selectedPackage ? selectedPackage.nights : 0,
    minDate,
    maxDate,
    language,
    startPlaceholder: t('calendar.arrivalDate'),
    endPlaceholder: t('calendar.departureDate'),
    clearLabel: t('calendar.clearDates'),
    displayFormat: toDateFnsToken(t('calendar.dateFormat')),
    adults: totalAdults,
    totalChildren,
    children,
  };
  // endregion

  const consultDisabled = packageSale
    ? !selectedPackage || !currentArrivalDate
    : false;

  return (
    <div className="card-body availability-wrapper">
      {packageSale && (
        <PackageSelector
          packages={packages}
          selectedPackageId={selectedPackageId}
          onPackageChange={onPackageChangeHandler}
        />
      )}
      <div className="row">
        {!isWidget && <div className="row-fluid calendarPropertyName">
          {name}
        </div>}
        <div className={`col-12 col-md-6 col-lg-6 pr-lg-0${packageSale ? ' pkg-calendar-col' : ''}`}>
          <div className="form-group mr-md-6 mb-6 w-lg-40">
            <DateRangeCalendar
              {...calendarProps}
              numberOfMonths={isWideCalendar ? 2 : 1}
            />
          </div>
        </div>
        <div className="col-12 col-md-6 col-lg-5 pl-lg-0">
          <div className="row availability">
            {showPersons && (
              <AvailabilityGuests
                totalAdults={totalAdults}
                totalChildren={totalChildren}
                children={children}
                maxAdults={maxTotalGuests}
                onPersonChange={onPersonChangeHandler}
                onChildAgeChange={onChildAgeChangeHandler}
                onAvailabilityFetchHandler={onGuestsConfirmHandler}
              />
            )}
            <div className="col-12 col-lg-5 text-center mb-3">
              <button
                className="btn btn-raised btn-default w-100 w-lg-auto"
                onClick={onAvailabilityFetchHandler}
                disabled={consultDisabled}
              >
                {fetching ? t('labels.consulting') : t('labels.consult')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Availability;
